import { app } from '@ircf/config';
import { formatNumber } from './time.js';

/**
 * Display formatting.
 *
 * The recurring theme here is the distinction between "zero" and "not recorded".
 * A client with no logged sessions must not see `0%` adherence as though it were
 * a measured fact, so every formatter takes `null` and renders an explicit
 * absence.
 */

/** Universal marker for a value that genuinely has no data behind it. */
export const NO_DATA = '-';

/**
 * Money from minor units. `null` renders as `Enquire`, because an unpriced
 * service must invite a conversation rather than imply it is free.
 */
export function formatCurrency(
  minorUnits: number | null | undefined,
  options: { currency?: string; nullLabel?: string; showFree?: boolean } = {},
): string {
  const { currency = app.currency, nullLabel = 'Enquire', showFree = true } = options;

  if (minorUnits === null || minorUnits === undefined) return nullLabel;
  if (minorUnits === 0 && showFree) return 'Complimentary';

  return new Intl.NumberFormat(app.locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);
}

export function formatPercent(
  value: number | null | undefined,
  options: { fractionDigits?: number; nullLabel?: string } = {},
): string {
  const { fractionDigits = 0, nullLabel = NO_DATA } = options;
  if (value === null || value === undefined || Number.isNaN(value)) return nullLabel;
  return `${formatNumber(value, fractionDigits)}%`;
}

/** A measurement with its unit, e.g. `82.4 kg`. */
export function formatMeasurement(
  value: number | null | undefined,
  unit: string,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NO_DATA;
  return `${formatNumber(value, fractionDigits)} ${unit}`;
}

/** Signed change, e.g. `−2.4 kg` or `+5 kg`, using a real minus sign. */
export function formatChange(
  value: number | null | undefined,
  unit: string,
  fractionDigits = 1,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return NO_DATA;
  if (value === 0) return `No change`;
  const sign = value > 0 ? '+' : '−';
  return `${sign}${formatNumber(Math.abs(value), fractionDigits)} ${unit}`;
}

/** `12.4 MB`, `840 kB`. Decimal units, matching how operating systems report. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${formatNumber(bytes / 1000, 0)} kB`;
  if (bytes < 1000 * 1000 * 1000) return `${formatNumber(bytes / (1000 * 1000), 1)} MB`;
  return `${formatNumber(bytes / (1000 * 1000 * 1000), 2)} GB`;
}

/** `1 session` / `3 sessions`. */
export function pluralise(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${formatNumber(count)} ${word}`;
}

/** Joins a list naturally: `strength, fitness and confidence`. */
export function formatList(items: readonly string[]): string {
  const filtered = items.filter((item) => item.trim().length > 0);
  if (filtered.length === 0) return NO_DATA;
  if (filtered.length === 1) return filtered[0] as string;
  return `${filtered.slice(0, -1).join(', ')} and ${filtered[filtered.length - 1]}`;
}

/** Truncates on a word boundary and appends an ellipsis. */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** Initials from a name, for avatar fallbacks. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** `+61 412 345 678` → tel: href. Strips display formatting only. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

/** Sentence case for enum-derived labels that arrive SCREAMING_SNAKE. */
export function humaniseEnum(value: string): string {
  const spaced = value.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** RPE / RIR / rating out of a maximum, e.g. `7 / 10`. */
export function formatRating(value: number | null | undefined, max: number): string {
  if (value === null || value === undefined) return NO_DATA;
  return `${formatNumber(value, Number.isInteger(value) ? 0 : 1)} / ${max}`;
}

/**
 * Renders a set as a coach would write it: `80 kg × 5`, `12 reps`, `45 s`.
 * Returns `null` when nothing was actually recorded, so callers can omit the
 * element entirely rather than render an empty string.
 */
export function formatSet(set: {
  reps: number | null;
  loadKg: number | null;
  durationSeconds: number | null;
  distanceMetres: number | null;
}): string | null {
  const { reps, loadKg, durationSeconds, distanceMetres } = set;

  if (loadKg !== null && reps !== null) return `${formatNumber(loadKg, 1)} kg × ${reps}`;
  if (reps !== null) return `${reps} reps`;
  if (durationSeconds !== null) {
    return durationSeconds >= 60
      ? `${formatNumber(durationSeconds / 60, 1)} min`
      : `${durationSeconds} s`;
  }
  if (distanceMetres !== null) {
    return distanceMetres >= 1000
      ? `${formatNumber(distanceMetres / 1000, 2)} km`
      : `${distanceMetres} m`;
  }
  return null;
}
